import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import "../../Styles/Order.css";
import Header from "../../Core/Header";
import { Section } from "../../Core/Section";
import {
  OrdersApi,
  OrdersApprovedAPI,
  OrdersListApi,
} from "../../Services/Operations/ProductAPI";
import { useSelector } from "react-redux";

const Order = () => {
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [entriesToShow, setEntriesToShow] = useState(10);
  const token = useSelector((state) => state.user.token);

  const [approvingId, setApprovingId] = useState(null);
  const [approvalError, setApprovalError] = useState(null);

  const handleApproveOrder = async (orderId) => {
    setApprovingId(orderId);
    setApprovalError(null);
    try {
      const response = await OrdersApprovedAPI(token, orderId);
      if (response.status === 200) {
        setOrders(
          orders.map((order) =>
            order.id === orderId ? { ...order, orderStatus: "DISPATCHED" } : order
          )
        );
      }
    } catch (error) {
      console.error("Approval failed:", error);
      setApprovalError("Failed to approve order. Please try again.");
    } finally {
      setApprovingId(null);
      setOpenDropdown(null);
    }
  };

  useEffect(() => {
    const fetchOrderList = async () => {
      try {
        const response = await OrdersListApi(token);
        if (response.data && response.data.length > 0) {
          setOrders(response.data);
        }
        setLoading(false);
      } catch (error) {
        console.error("Error:", error);
        setLoading(false);
      }
    };

    fetchOrderList();
  }, [token]);

  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    const date = new Date(Number(timestamp));
    return date.toLocaleDateString("en-GB");
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return "₹0.00";
    return `₹${parseFloat(amount).toFixed(2)}`;
  };

  const getStatusClass = (status) => {
    console.log("status : ",status)
    switch (status) {
      case "PENDING":
        return "status pending";
      case "PAID":
        return "status paid";
      case "DISPATCHED":
        return "status DISPATCHED";
      case "APPROVED":
        return "status paid";
      default:
        return "status";
    }
  };

  const filteredOrders = orders.filter((order) => {
    const searchTermLower = searchTerm.toLowerCase();
    return (
      order.storeName?.toLowerCase().includes(searchTermLower) ||
      formatDate(order.orderDate).toLowerCase().includes(searchTermLower) ||
      formatCurrency(order.grandTotal)
        .toLowerCase()
        .includes(searchTermLower) ||
      order.orderStatus?.toLowerCase().includes(searchTermLower)
    );
  });

  const visibleOrders = filteredOrders.slice(0, entriesToShow);

  const toggleDropdown = (id) => {
    console.log("id : ", id);
    setOpenDropdown(openDropdown === id ? null : id);
  };

  const closeModal = () => {
    setSelectedOrder(null);
  };

  const backdrop = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { duration: 0.3 },
    },
    exit: { opacity: 0 },
  };

  const modal = {
    hidden: {
      y: 50,
      opacity: 0,
      scale: 0.95,
    },
    visible: {
      y: 0,
      opacity: 1,
      scale: 1,
      transition: {
        type: "spring",
        damping: 25,
        stiffness: 500,
        delay: 0.1,
      },
    },
    exit: {
      y: 50,
      opacity: 0,
      scale: 0.95,
      transition: { duration: 0.2 },
    },
  };

  const itemAnimation = {
    hidden: { opacity: 0, x: -20 },
    visible: (i) => ({
      opacity: 1,
      x: 0,
      transition: {
        delay: i * 0.05,
        type: "spring",
        stiffness: 300,
      },
    }),
    exit: { opacity: 0 },
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05,
        delayChildren: 0.2,
      },
    },
  };

  console.log("selected order : ", selectedOrder);

  return (
    <div id="main_content">
      <Header />
      <div className="page">
        <Section />

        <div className="order-container">
          <motion.h2 initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            Order List
          </motion.h2>

          <motion.div
            className="order-actions"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <label>
              Show
              <select
                value={entriesToShow}
                onChange={(e) => setEntriesToShow(Number(e.target.value))}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              entries
            </label>
            <input
              type="text"
              placeholder="Search Orders..."
              className="search-input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </motion.div>

          {loading ? (
            <div className="loading-spinner">Loading orders...</div>
          ) : (
            <div className="table-responsive">
              <table>
                <thead>
                  <tr>
                    <th>Order Date</th>
                    <th>Store Name</th>
                    <th>Total</th>
                    <th>CGST</th>
                    <th>SGST</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  <AnimatePresence>
                    {visibleOrders.length > 0 ? (
                      visibleOrders.map((order) => (
                        <motion.tr
                          key={order.id}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          whileHover={{ scale: 1.01 }}
                          transition={{ type: "spring", stiffness: 500 }}
                        >
                          <td>{formatDate(order.orderDate)}</td>
                          <td>{order.storeName || "N/A"}</td>
                          <td>{formatCurrency(order.grandTotal)}</td>
                          <td>{formatCurrency(order.totalCgst)}</td>
                          <td>{formatCurrency(order.totalSgst)}</td>
                          <td>
                            <p className={getStatusClass(order.orderStatus)}>
                              {order.orderStatus || "N/A"}
                            </p>
                          </td>
                          <td
                            style={{ position: "relative" }}
                            className="dropdown-container"
                          >
                            <motion.button
                              className="action-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleDropdown(order.id);
                              }}
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                            >
                              Action ▼
                            </motion.button>
                            <AnimatePresence>
                              {openDropdown === order.id && (
                                <motion.div
                                  className="dropdown1"
                                  initial={{ opacity: 0, y: -10 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  exit={{ opacity: 0, y: -10 }}
                                  transition={{ duration: 0.2 }}
                                >
                                  <button
                                    onClick={() => {
                                      setSelectedOrder(order);
                                      setOpenDropdown(null);
                                    }}
                                    style={{ color: "black" }}
                                  >
                                    View
                                  </button>
                                  {
                                    order.orderStatus !== "DISPATCHED" && <button
                                    style={{ color: "black" }}
                                    className="delete-btn"
                                    onClick={() => handleApproveOrder(order.id)}
                                    disabled={approvingId === order.id}
                                  >
                                    {approvingId === order.id
                                      ? "Approving..."
                                      : "Approve"}
                                  </button>
                                  }
                                  
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </td>
                        </motion.tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="7" className="no-orders">
                          No orders found
                        </td>
                      </tr>
                    )}
                  </AnimatePresence>
                </tbody>
              </table>
            </div>
          )}
        </div>

        <AnimatePresence>
          {selectedOrder && (
            <motion.div
              className="modal-backdrop"
              variants={backdrop}
              initial="hidden"
              animate="visible"
              exit="exit"
              onClick={closeModal}
            >
              <motion.div
                className="modal-content enhanced-modal"
                variants={modal}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="modal-header">
                  <motion.h2
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                  >
                    Order #{selectedOrder.id || "N/A"}
                  </motion.h2>
                  <motion.button
                    className="close-btn"
                    onClick={closeModal}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    &times;
                  </motion.button>
                </div>

                <motion.div
                  className="order-info"
                  variants={staggerContainer}
                  initial="hidden"
                  animate="visible"
                >
                  <motion.div variants={itemAnimation}>
                    <div className="info-item">
                      <span className="info-label">Store:</span>
                      <span className="info-value">
                        {selectedOrder.storeName || "N/A"}
                      </span>
                    </div>
                  </motion.div>
                  <motion.div variants={itemAnimation}>
                    <div className="info-item">
                      <span className="info-label">Order Date:</span>
                      <span className="info-value">
                        {formatDate(selectedOrder.orderDate)}
                      </span>
                    </div>
                  </motion.div>
                  <motion.div variants={itemAnimation}>
                    <div className="info-item">
                      <span className="info-label">Status:</span>
                      <span
                        className={`info-value ${getStatusClass(
                          selectedOrder.orderStatus
                        )}`}
                      >
                        {selectedOrder.orderStatus || "N/A"}
                      </span>
                    </div>
                  </motion.div>
                </motion.div>

                <div className="items-table-container">
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.4 }}
                  >
                    <h3 className="items-title">Order Items</h3>
                    <table>
                      <thead>
                        <tr>
                          <th>Product ID</th>
                          <th>Quantity</th>
                          <th>Rate</th>
                          <th>CGST</th>
                          <th>SGST</th>
                          <th>IGST</th>
                          <th>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        <AnimatePresence>
                          {selectedOrder.items?.map((item, index) => (
                            <motion.tr
                              key={index}
                              custom={index}
                              initial="hidden"
                              animate="visible"
                              exit="exit"
                              variants={itemAnimation}
                              whileHover={{
                                backgroundColor: "rgba(74, 108, 247, 0.05)",
                                transition: { duration: 0.1 },
                              }}
                            >
                              <td>{item.productId || "N/A"}</td>
                              <td>{item.quantity || "N/A"}</td>
                              <td>{formatCurrency(item.sellRate)}</td>
                              <td>
                                {formatCurrency(item.sellCgstAmt)} (
                                {item.sellCgst || 0}%)
                              </td>
                              <td>
                                {formatCurrency(item.sellSgstAmt)} (
                                {item.sellSgst || 0}%)
                              </td>
                              <td>
                                {formatCurrency(item.sellIgstAmt)} (
                                {item.sellIgst || 0}%)
                              </td>
                              <td>
                                {formatCurrency(
                                  (item.sellRate || 0) * (item.quantity || 1) +
                                    (item.sellCgstAmt || 0) +
                                    (item.sellSgstAmt || 0) +
                                    (item.sellIgstAmt || 0)
                                )}
                              </td>
                            </motion.tr>
                          ))}
                        </AnimatePresence>
                      </tbody>
                    </table>
                  </motion.div>
                </div>

                <motion.div
                  className="order-summary"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                >
                  <h3 className="summary-title">Order Summary</h3>
                  <div className="summary-row">
                    <span>Subtotal:</span>
                    <span>
                      {formatCurrency(
                        selectedOrder.grandTotal -
                          (selectedOrder.totalCgst || 0) -
                          (selectedOrder.totalSgst || 0) -
                          (selectedOrder.totalIgst || 0)
                      )}
                    </span>
                  </div>
                  <div className="summary-row">
                    <span>CGST:</span>
                    <span>{formatCurrency(selectedOrder.totalCgst)}</span>
                  </div>
                  <div className="summary-row">
                    <span>SGST:</span>
                    <span>{formatCurrency(selectedOrder.totalSgst)}</span>
                  </div>
                  <div className="summary-row">
                    <span>IGST:</span>
                    <span>{formatCurrency(selectedOrder.totalIgst)}</span>
                  </div>
                  <div className="summary-row grand-total">
                    <span>Grand Total:</span>
                    <span>{formatCurrency(selectedOrder.grandTotal)}</span>
                  </div>
                </motion.div>

                <motion.div
                  className="modal-footer"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.6 }}
                >
                  <motion.button
                    className="print-btn"
                    onClick={() => window.print()}
                  >
                    Print Invoice
                  </motion.button>
                  <motion.button
                    className="close-modal-btn"
                    onClick={closeModal}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Close
                  </motion.button>
                </motion.div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default Order;
